import React from 'react';
import Text from '../../../../components/Typography/Text';
import TextField from '../../../../components/Textfields/TextField';
import PhoneNumber from '../../../../components/Textfields/PhoneNumber';
import {
  SelectAccountType,
  SelectSubAccountType,
} from '../../../../components/Dropdown/SelectAccountType';
import LinkButton from '../../../../components/Buttons/LinkButton';
import AutoCompleteCountry from '../../../../components/AutoComplete/AutoCompleteCountry';
import MaskedField from '../../../../components/Textfields/MaskedField';
import FormAccountAddressPrimary from '../Common/FormAccountAddressPrimary';
import FormAccountOwnerJoint from '../Common/FormAccountOwnerJoint';
import FormAccountOwnerCompany from '../Common/FormAccountOwnerCompany';
import { useAccountContext } from '../Data/accountContext';
import FormBase from '../FormBase';
import { saveRegistrationAccount } from '../../../../services/RegistrationService';
import { validateAccount, hasError } from '../Data/validation';
import SelectAutoCompleteRepAdvisor from '../../../../components/AutoComplete/SelectAutoCompleteRepAdvisor';
import {
  attachFiles,
  deleteFile,
} from '../../../../services/CommonService';
import Button from '../../../../components/Buttons/Button';

export default function FormAccount() {
  const { account, setAccount, isBussinessAccount, setAccountResponse, disableUpdate, dynamicCompanyAttachments, setDynamicCompanyAttachments } =
    useAccountContext();

  const [validation, setValidation] = React.useState({
    primaryOwner: {},
    corpOwnersList: [],
  });

  const handlePrimaryOwnerChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;
    const copy = { ...account };
    copy.primaryOwner[input.name] =
      input.type === 'checkbox' ? input.checked : input.value;
    setAccount(copy);
  };

  const handleChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;
    const copy = { ...account };

    if (input.name === 'accountType') {
      copy.subType = '';

      if (input.value === 'C') {
        copy.accountName = '';
        if (!copy.primaryOwner.secondaryPhone) {
          copy.primaryOwner.secondaryPhone = account.primaryOwner.phone;
          copy.primaryOwner.phone = '';
          copy.primaryOwner.email = '';
        }
      }
    }

    copy[input.name] =
      input.type === 'checkbox' ? input.checked : input.formattedValue || input.value;
    setAccount(copy);
  };

  const handleChangeFormSubHeader = (accountType) => {
    if (accountType === 'J') {
      return 'Primary Applicant';
    } else if (accountType === 'C') {
      return 'Company Information';
    } else {
      return 'Basic Information';
    }
  };

  const handleSave = async () => {
    const errs = validateAccount(account, isBussinessAccount(), dynamicCompanyAttachments);
    setValidation(errs);
    if (hasError(errs)) {
      console.log('account errs', errs);
      return { acct: account, err: true };
    }

    if (account?.accountType !== 'C') {
      if (Array.isArray(dynamicCompanyAttachments) && dynamicCompanyAttachments.length > 0) {

        for (const companyAttachment of dynamicCompanyAttachments) {
          if (companyAttachment.id) {
            await deleteFile(companyAttachment.id);
          }
        }

        setDynamicCompanyAttachments((prevData) =>
          prevData.map((attachment) => ({
            ...attachment,
            fileName: '',
            id: 0,
            tag: ''
          }))
        );
      }
    }


    const res = await saveRegistrationAccount(account);
    setAccountResponse(res);
    return { acct: res, err: false };
  };

  return (
    <FormBase header="Account Information" subHeader="" faq="Account" save={handleSave}>
      <form className="account-form-w" autoComplete="off">
        <input type="hidden" autoComplete="off" />
        <div className="grd-row">
          <div className="grd-cell">
            <SelectAccountType
              required={true}
              name="accountType"
              label="Account Type"
              placeholder="Account Type"
              value={account.accountType}
              onChange={handleChange}
            />
          </div>
          {['J', 'C', 'IRA'].includes(account.accountType) && (
            <div className="grd-cell">
              <SelectSubAccountType
                tooltip={
                  account.accountType === 'J' &&
                  (account.subType === 'Rights of Survivorship'
                    ? 'Have an equal rights to the accounts asset and are afforded survivor rights'
                    : account.subType === 'Community Property'
                      ? 'Joint ownership assets between married couples. States inclusion: WA, ID, NV, CA, WZ, TX, and LA'
                      : account.subType === 'Tenants in Common'
                        ? 'Joint ownership of assets but no survivorship afforded.'
                        : 'Select a joint account type first to see info of that specific account')
                }
                required={true}
                error={!!validation?.subType}
                msg={validation?.subType}
                name="subType"
                value={account.subType}
                label="Sub Account Type"
                placeholder="Sub Account Type"
                onChange={handleChange}
                selectedAccountType={account.accountType}
              ></SelectSubAccountType>
            </div>
          )}
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <SelectAutoCompleteRepAdvisor
              fullWidth
              tooltip="The representative that invited you to create an account."
              name="rep"
              label="Rep/Advisor"
              value={account.rep}
              correspondent={account.correspondent}
              onChange={handleChange}
              inputProps={{ maxLength: 150 }}
              InputLabelProps={{ shrink: true }}
            ></SelectAutoCompleteRepAdvisor>
          </div>
        </div>
        <Text
          mt={40}
          mb={25}
          variant="h3"
          label={handleChangeFormSubHeader(account.accountType)}
        />

        {account.accountType === 'C' && (
          <React.Fragment>
            <div className="grd-row">
              <div className="grd-cell">
                <TextField
                  required={true}
                  error={!!validation?.accountName}
                  msg={validation?.accountName}
                  max={100}
                  name="accountName"
                  label="Company Name"
                  placeholder="Company Name"
                  type="text"
                  value={account.accountName}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="grd-row">
              <div className="grd-cell">
                <AutoCompleteCountry
                  required={true}
                  error={!!validation?.formationCountry}
                  msg={validation?.formationCountry}
                  name="formationCountry"
                  label="Formation Country"
                  placeholder="Formation Country"
                  value={account.formationCountry}
                  onChange={handleChange}
                />
              </div>
              <div className="grd-cell">
                <MaskedField
                  required={true}
                  error={!!validation?.tin}
                  msg={validation?.tin}
                  name="tin"
                  label="EIN or Tax ID"
                  placeholder="EIN or Tax ID"
                  format="##-#######"
                  value={account.tin}
                  onChange={handleChange}
                />
              </div>
            </div>
          </React.Fragment>
        )}

        <FormAccountAddressPrimary validation={validation} />

        <div className="grd-row">
          {isBussinessAccount() && (
            <div className="grd-cell">
              <PhoneNumber
                required={true}
                error={!!validation?.primaryOwner?.phone}
                msg={validation?.primaryOwner?.phone}
                name="phone"
                label="Business Phone Number"
                value={account.primaryOwner?.phone}
                onChange={handlePrimaryOwnerChange}
              />
            </div>
          )}
        </div>
        {account.accountType === 'C' && (
          <>
            {Array.isArray(dynamicCompanyAttachments) && dynamicCompanyAttachments.length > 0 ? (
              <>
                <Text mt={40} mb={25} variant="h3" label="Company Attachment" />
                {dynamicCompanyAttachments.map((companyAttachment, index) => (
                  <div key={index}>
                    <div className="grd-row" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Text variant="subtitle2" label={`${companyAttachment.requiredAttachment}:`} />
                      <input
                        onChange={async (e) => {
                          if (companyAttachment.id) {
                            await deleteFile(companyAttachment.id);
                          }
                          const res = await attachFiles(
                            account.accountId,
                            'ClientAccount',
                            e.target.files,
                            companyAttachment.requiredAttachment,
                          );
                          if (res) {
                            setDynamicCompanyAttachments((prevAttachments) =>
                              prevAttachments.map((att, i) =>
                                i === index
                                  ? { ...att, fileName: res[0]?.fileName || att.fileName, id: res[0]?.id || 0 }
                                  : att
                              )
                            );
                            setValidation((prevData) => ({ ...prevData, [`attachment${index}`]: '' }));
                          }
                        }}
                        accept="*/*"
                        id={companyAttachment.requiredAttachment + index}
                        style={{ display: 'none' }}
                        type="file"
                      />
                      <label htmlFor={companyAttachment.requiredAttachment + index}>
                        <Button type="attachment" component="span" label="Upload File" size="small" />
                      </label>
                      <Text
                        mt={0}
                        ml={0}
                        error={!!validation[`attachment${index}`]}
                        label={
                          validation[`attachment${index}`]
                            ? validation[`attachment${index}`]
                            : companyAttachment.fileName
                        }
                        variant="subtitle1"
                        color={validation[`attachment${index}`] ? 'red' : ''}
                      />
                    </div>
                  </div>
                ))}
              </>
            ) : null}
          </>
        )}


        {account.accountType === 'J' && <FormAccountOwnerJoint validation={validation} />}
        {account.accountType === 'C' && (
          <div>
            <FormAccountOwnerCompany objectName="primaryOwner" validation={validation} />
            {Array.isArray(account?.corpOwnersList) && account.corpOwnersList.length > 0 ? (
              account.corpOwnersList.map((_, index) => (
                <FormAccountOwnerCompany
                  index={index}
                  key={index}
                  objectName="corpOwnersList"
                  validation={validation}
                />
              ))
            ) : null}

            <span
              style={{
                float: 'right',
                margin: '20px 0px',
              }}
            >
              {!disableUpdate && (
                <LinkButton
                  type="add"
                  label={'Add Officer/Owner'}
                  onClick={() => {
                    let copy = { ...account };
                    copy.corpOwnersList.push({
                      identification: {},
                    });
                    setAccount(copy);
                  }}
                />
              )}
            </span>
          </div>
        )}
      </form>
    </FormBase>
  );
}
